import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import AsyncSelect from "react-select/async";
import { components } from "react-select";
import { FormFeedback } from "reactstrap";

import { formatClientPickerLabel } from "../../helpers/clientPhone";
import {
  buildClientNotesTooltip,
  getClientNotesPinLine,
  hasClientOperationalNotes,
} from "../../helpers/clientNotes";
import {
  getServicePreferencesPreview,
  hasClientServicePreferences,
} from "../../helpers/clientServicePreferences";
import { Client, getClientById, listClients } from "../../services/clientService";
import ClientNotesIndicator from "./ClientNotesIndicator";
import ClientNotesPreviewModal from "./ClientNotesPreviewModal";
import ClientServicePreferencesIndicator from "./ClientServicePreferencesIndicator";
import ClientTagChips from "./ClientTagChips";
import QuickClientCreateModal from "./QuickClientCreateModal";

const SEARCH_DEBOUNCE_MS = 300;
const MIN_SEARCH_LENGTH = 2;
const RESULTS_PER_PAGE = 8;

export type ClientPickerOption = {
  value: string;
  label: string;
  client: Client;
};

export type ClientPickerProps = {
  id?: string;
  name?: string;
  value: string;
  onChange: (clientId: string) => void;
  onBlur?: () => void;
  error?: string;
  invalid?: boolean;
  isDisabled?: boolean;
};

const toOption = (client: Client): ClientPickerOption => ({
  value: String(client.id),
  label: formatClientPickerLabel(client.name, client.country_code, client.phone_number),
  client,
});

const ClientPicker = ({
  id,
  name,
  value,
  onChange,
  onBlur,
  error,
  invalid = false,
  isDisabled = false,
}: ClientPickerProps) => {
  const { t } = useTranslation();
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [selectedOption, setSelectedOption] = useState<ClientPickerOption | null>(null);
  const [quickCreateOpen, setQuickCreateOpen] = useState(false);
  const [notesPreview, setNotesPreview] = useState<{
    notes: string;
    clientName: string;
  } | null>(null);

  useEffect(() => {
    if (!value) {
      setSelectedOption(null);
      return;
    }

    if (selectedOption?.value === value) {
      return;
    }

    const clientId = Number(value);

    if (Number.isNaN(clientId)) {
      setSelectedOption(null);
      return;
    }

    let cancelled = false;

    getClientById(clientId)
      .then((response) => {
        if (!cancelled) {
          setSelectedOption(toOption(response.data));
        }
      })
      .catch(() => {
        if (!cancelled) {
          setSelectedOption(null);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [selectedOption?.value, value]);

  const loadOptions = useCallback(
    (inputValue: string): Promise<ClientPickerOption[]> =>
      new Promise((resolve) => {
        if (debounceTimerRef.current) {
          clearTimeout(debounceTimerRef.current);
        }

        debounceTimerRef.current = setTimeout(async () => {
          const term = inputValue.trim();

          if (term.length < MIN_SEARCH_LENGTH) {
            resolve([]);
            return;
          }

          try {
            const response = await listClients({
              search: term,
              per_page: RESULTS_PER_PAGE,
            });
            resolve(response.data.map(toOption));
          } catch {
            resolve([]);
          }
        }, SEARCH_DEBOUNCE_MS);
      }),
    []
  );

  const handleChange = (option: ClientPickerOption | null) => {
    setSelectedOption(option);
    onChange(option?.value ?? "");
  };

  const handleQuickCreateSuccess = (client: Client) => {
    const option = toOption(client);
    setSelectedOption(option);
    onChange(String(client.id));
    setQuickCreateOpen(false);
  };

  const MenuList = useMemo(() => {
    const CustomMenuList = (props: any) => (
      <components.MenuList {...props}>
        {props.children}
        {!isDisabled ? (
          <div className="border-top px-3 py-2">
            <button
              type="button"
              className="btn btn-link btn-sm p-0 text-primary"
              onMouseDown={(event) => {
                event.preventDefault();
                event.stopPropagation();
              }}
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                setQuickCreateOpen(true);
              }}
            >
              {t("studio.clients.picker.add_new")}
            </button>
          </div>
        ) : null}
      </components.MenuList>
    );

    CustomMenuList.displayName = "ClientPickerMenuList";

    return CustomMenuList;
  }, [isDisabled, t]);

  const buildNotesTooltip = useCallback(
    (notes: string) =>
      buildClientNotesTooltip(notes, (preview) =>
        t("clients.picker.notesTooltip", { preview })
      ),
    [t]
  );

  const openNotesPreview = useCallback((client: Client) => {
    if (!hasClientOperationalNotes(client.notes)) {
      return;
    }

    setNotesPreview({
      notes: client.notes!.trim(),
      clientName: client.name,
    });
  }, []);

  const selectComponents = useMemo(() => {
    const Option = (props: any) => (
      <components.Option {...props}>
        <div className="d-flex flex-column w-100 gap-1">
          <div className="d-flex align-items-center justify-content-between w-100 gap-2">
            <span className="text-truncate">{props.data.label}</span>
            {hasClientOperationalNotes(props.data.client.notes) ? (
              <ClientNotesIndicator
                tooltip={buildNotesTooltip(props.data.client.notes!.trim())}
                ariaLabel={t("clients.picker.viewNotesAria")}
                preview={getClientNotesPinLine(props.data.client.notes, 48)}
                onOpen={() => openNotesPreview(props.data.client)}
              />
            ) : null}
            {hasClientServicePreferences(props.data.client.service_preferences) ? (
              <ClientServicePreferencesIndicator
                tooltip={t("clients.picker.preferencesTooltip", {
                  preview:
                    getServicePreferencesPreview(props.data.client.service_preferences) ??
                    t("clients.preferences.chipFallback"),
                })}
                ariaLabel={t("clients.picker.viewPreferencesAria")}
                preview={getServicePreferencesPreview(
                  props.data.client.service_preferences,
                  1,
                  48
                )}
              />
            ) : null}
          </div>
          <ClientTagChips tags={props.data.client.tags} maxVisible={2} />
        </div>
      </components.Option>
    );

    const SingleValue = (props: any) => (
      <components.SingleValue {...props}>
        <div className="d-flex align-items-center gap-1">
          <span className="text-truncate">{props.data.label}</span>
          {hasClientOperationalNotes(props.data.client.notes) ? (
            <ClientNotesIndicator
              tooltip={buildNotesTooltip(props.data.client.notes!.trim())}
              ariaLabel={t("clients.picker.viewNotesAria")}
              onOpen={() => openNotesPreview(props.data.client)}
            />
          ) : null}
          {hasClientServicePreferences(props.data.client.service_preferences) ? (
            <ClientServicePreferencesIndicator
              tooltip={t("clients.picker.preferencesTooltip", {
                preview:
                  getServicePreferencesPreview(props.data.client.service_preferences) ??
                  t("clients.preferences.chipFallback"),
              })}
              ariaLabel={t("clients.picker.viewPreferencesAria")}
            />
          ) : null}
          <ClientTagChips tags={props.data.client.tags} maxVisible={1} />
        </div>
      </components.SingleValue>
    );

    Option.displayName = "ClientPickerOption";
    SingleValue.displayName = "ClientPickerSingleValue";

    return { MenuList, Option, SingleValue };
  }, [MenuList, buildNotesTooltip, openNotesPreview, t]);

  const selectStyles = useMemo(
    () => ({
      control: (base: any, state: any) => ({
        ...base,
        minHeight: "38px",
        borderColor: invalid
          ? "var(--vz-form-invalid-border-color, #f06548)"
          : state.isFocused
            ? "var(--vz-primary)"
            : base.borderColor,
        boxShadow: invalid
          ? "0 0 0 0.1rem rgba(var(--vz-danger-rgb, 240, 101, 72), 0.25)"
          : state.isFocused
            ? "0 0 0 0.1rem rgba(var(--vz-primary-rgb, 15, 76, 58), 0.25)"
            : base.boxShadow,
        "&:hover": {
          borderColor: invalid
            ? "var(--vz-form-invalid-border-color, #f06548)"
            : state.isFocused
              ? "var(--vz-primary)"
              : base.borderColor,
        },
      }),
      menu: (base: any) => ({
        ...base,
        zIndex: 20,
      }),
    }),
    [invalid]
  );

  const noOptionsMessage = ({
    inputValue,
  }: {
    inputValue: string;
  }): string => {
    if (inputValue.trim().length < MIN_SEARCH_LENGTH) {
      return t("studio.clients.picker.type_more");
    }

    return t("studio.clients.picker.no_results");
  };

  return (
    <>
      <AsyncSelect
        inputId={id}
        name={name}
        instanceId={id ?? "client-picker"}
        classNamePrefix="client-picker"
        className={invalid ? "is-invalid" : undefined}
        cacheOptions
        defaultOptions={false}
        loadOptions={loadOptions}
        value={selectedOption}
        onChange={handleChange}
        onBlur={onBlur}
        isClearable
        isDisabled={isDisabled}
        placeholder={t("studio.clients.picker.placeholder")}
        loadingMessage={() => t("studio.clients.picker.loading")}
        noOptionsMessage={noOptionsMessage}
        components={selectComponents}
        styles={selectStyles}
        isSearchable
      />
      {invalid && error ? <FormFeedback className="d-block">{error}</FormFeedback> : null}

      <QuickClientCreateModal
        isOpen={quickCreateOpen}
        onClose={() => setQuickCreateOpen(false)}
        onSuccess={handleQuickCreateSuccess}
      />

      <ClientNotesPreviewModal
        isOpen={Boolean(notesPreview)}
        notes={notesPreview?.notes ?? ""}
        clientName={notesPreview?.clientName}
        onClose={() => setNotesPreview(null)}
      />
    </>
  );
};

export default ClientPicker;
